import { ALBUM_GET_ID, ALBUM_VOTE_MULTIMEDIA } from 'constants/api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useSession } from 'contexts/session-context'
import { PIT_STOP_IDS } from '../constants'
import {
  F1GalleryPhoto,
  F1_VOTED_STORAGE_KEY,
  F1_VOTE_ERROR,
  applyVote,
  markVoted,
  normalizePhotos,
  readVotedIds,
  selectVisiblePhotos,
  sortByVotes
} from '../gallery'

export type F1VoteResult = 'ok' | 'alreadyVoted' | 'maxVotes' | 'error'

// Gallery data for the Pit Stop 4 landing sections. Mirrors the direct-axios
// pattern of trivia-context: no AlbumProvider, one GET for the whole rally.
export const usePitStopGallery = (enabled = true) => {
  const [{ data: session }] = useSession()
  const [photos, setPhotos] = useState<F1GalleryPhoto[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [votedIds, setVotedIds] = useState<number[]>(() => readVotedIds())

  const idToken = ((session as any)?._tokenResponse as any)?.idToken

  const buildHeaders = useCallback(() => {
    const headers: Record<string, string> = { 'Ph-Api-Id': `${PH_API_ID ?? 1}` }
    // Visitors browse the gallery without Authorization
    if (idToken) headers.Authorization = `Bearer ${idToken}`
    return headers
  }, [idToken])

  const fetchGallery = useCallback(
    async (silent = false) => {
      if (PIT_STOP_IDS.photoAlbumId === null) {
        setLoading(false)
        setError(true)
        return
      }
      if (!silent) setLoading(true)
      try {
        const { data } = await axios.get(`${ALBUM_GET_ID}${PIT_STOP_IDS.photoAlbumId}`, {
          headers: buildHeaders()
        })
        // The real API ships every numeric field as a string: coerce first
        setPhotos(selectVisiblePhotos(normalizePhotos(data?.multimedias)))
        setError(false)
      } catch (requestError) {
        if (!silent) {
          setPhotos([])
          // Visitors get a 401 by contract: that is the normal "no gallery"
          // mode (copy + login CTA), not an error state.
          setError(Boolean(idToken))
        }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [buildHeaders, idToken]
  )

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    fetchGallery()
  }, [enabled, fetchGallery])

  const vote = useCallback(
    async (photo: F1GalleryPhoto): Promise<F1VoteResult> => {
      // Optimistic: count + voted mark first, corrected on failure
      setPhotos((prev) => applyVote(prev, photo.id))
      setVotedIds(markVoted(photo.id))
      try {
        await axios.post(
          `${ALBUM_VOTE_MULTIMEDIA}?rallyMultimediaId=${photo.id}`,
          {},
          { headers: buildHeaders() }
        )
        // Silent refetch keeps counts in sync with other voters
        fetchGallery(true)
        return 'ok'
      } catch (requestError) {
        const code = (requestError as any)?.response?.data?.code
        setPhotos((prev) =>
          prev.map((item) => (item.id === photo.id ? { ...item, votes: photo.votes } : item))
        )
        if (code === F1_VOTE_ERROR.alreadyVoted) {
          // The backend already counted this device/user: keep it marked
          return 'alreadyVoted'
        }
        setVotedIds((prev) => prev.filter((id) => id !== photo.id))
        try {
          window.localStorage.setItem(
            F1_VOTED_STORAGE_KEY,
            JSON.stringify(readVotedIds().filter((id) => id !== photo.id))
          )
        } catch (storageError) {
          // Storage unavailable: in-memory state already corrected
        }
        if (code === F1_VOTE_ERROR.maxVotes) return 'maxVotes'
        return 'error'
      }
    },
    [buildHeaders, fetchGallery]
  )

  const ranking = useMemo(() => sortByVotes(photos), [photos])

  return { photos, ranking, loading, error, votedIds, fetchGallery, vote }
}

export default usePitStopGallery
